/**
 * `MarketBoost` (boost.v0) → boosters-leaderboard row mapper.
 *
 * Subject: `morfi.market.${m}.user.*.boost.v0` (proto
 * `morfi.v1.MarketBoost`, payload `{ actor, amountD6, ts, … }`).
 *
 * The boosters leaderboard is a per-booster aggregate (one row per
 * booster, columns: `totalAmount`, `numBoosts`, `lastBoostAt`). The
 * codegen-emitted caller invokes this helper for EVERY cached row on
 * each boost event; the helper short-circuits unless the row matches
 * the event's `actor` (compared against `boosterPubkey` first, falling
 * back to `booster.walletAddress` when the row was projected with the
 * nested booster identity).
 *
 * On a match: `totalAmount += amountD6`, `numBoosts += 1`,
 * `lastBoostAt = ts`. The schema also exposes a per-boost `amount`
 * field (the single-event "active" view); this helper does NOT touch
 * it because aggregating it would muddle the two view shapes — the
 * leaderboard cache entry is the canonical aggregate and `amount`
 * stays at the most-recent boost's amount, written by the server on
 * each REST refetch.
 *
 * NEW BOOSTERS (no row in the cache) are NOT prepended here — the
 * helper only sees existing rows. A brand-new booster appears on the
 * next REST refetch. This is acceptable because (a) the leaderboard
 * is sorted by aggregate amount so a first boost rarely jumps into a
 * visible position immediately and (b) adding a prepend recipe to
 * the same subject would need synthetic data (rank, displayName,
 * avatarUrl) the event payload doesn't carry. A future enrichment
 * pass via an id-then-fetch `prepend:` recipe on a sibling subject
 * could close the gap; for now realtime keeps existing rows hot.
 *
 * Immutable: returns a NEW object only when fields change; otherwise
 * the exact `prev` reference (idempotent on replay, keeps structural-
 * sharing short-circuits intact). Platform-agnostic — pure data, no
 * web/RN API. Safe in core.
 */

import { addD6String } from './d6.js';

function coerceAmountD6(amount: unknown): string | null {
  if (typeof amount === 'bigint') return amount.toString();
  if (typeof amount === 'number' && Number.isFinite(amount)) {
    return Math.trunc(amount).toString();
  }
  if (typeof amount === 'string') {
    const trimmed = amount.trim();
    if (trimmed === '' || !/^-?\d+$/.test(trimmed)) return null;
    return trimmed;
  }
  return null;
}

/** Boost-event `ts` is seconds-or-ms epoch (indexer-wired number). Normalise
 *  to an ISO-8601 string matching the REST `lastBoostAt` shape, or null when
 *  the value is unparseable / out of range. */
function coerceTimestampIso(raw: unknown): string | null {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  const ms = n > 1e12 ? n : n * 1000;
  return new Date(ms).toISOString();
}

export function applyBoostToLeaderboardRow<
  T extends {
    readonly boosterPubkey?: string;
    readonly booster?: { readonly walletAddress?: string };
    readonly totalAmount?: string;
    readonly numBoosts?: number;
    readonly lastBoostAt?: string | null;
  },
>(prev: T, raw: Record<string, unknown>): T {
  const actor = typeof raw.actor === 'string' ? raw.actor : undefined;
  if (actor === undefined) return prev;
  const rowActor = prev.boosterPubkey ?? prev.booster?.walletAddress;
  if (rowActor === undefined || rowActor !== actor) return prev;

  const amount = coerceAmountD6(raw.amountD6);
  if (amount === null || amount === '0') return prev;
  const lastBoostAt = coerceTimestampIso(raw.ts);

  const prevTotal = prev.totalAmount;
  const nextTotal = prevTotal !== undefined ? addD6String(prevTotal, amount) : undefined;
  const prevCount = prev.numBoosts;
  const nextCount = prevCount !== undefined ? prevCount + 1 : undefined;

  const totalChanged = nextTotal !== undefined && nextTotal !== prevTotal;
  const countChanged = nextCount !== undefined && nextCount !== prevCount;
  const tsChanged = lastBoostAt !== null && lastBoostAt !== prev.lastBoostAt;

  if (!totalChanged && !countChanged && !tsChanged) return prev;

  return {
    ...prev,
    ...(totalChanged ? { totalAmount: nextTotal } : {}),
    ...(countChanged ? { numBoosts: nextCount } : {}),
    ...(tsChanged ? { lastBoostAt } : {}),
  };
}
